import { tr } from '@/lib/i18n-core';
import { useMemo, useRef, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react';
import { Plus } from 'lucide-react';
import { cn, humanize, initials } from '@/lib/utils';
import { useReducedMotion } from '@/lib/motion';
import { avatarStyle, isShownOnCalendar, providerStyle, sessionBackground } from './calendar-colors';
import { layoutLanes, rowOccupancy, stripGeometry } from './calendar-layout';
import {
  axisMarks,
  gridHeight,
  hourMarks,
  isoAtMinute,
  minuteLabel,
  minuteOf,
  minuteToPx,
  overlapsDay,
  providerSpans,
  pxToMinute,
  slotRows,
  snapToSessions,
  type SessionSpan,
  type SlotRow,
} from './calendar-time';
import type { CalendarAppointment, CalendarColumn, CalendarDay, CalendarProvider, CalendarSnapshot } from './calendar-types';
import { CalendarEvent } from './CalendarEvent';
import type { DragState } from './useCalendarDrag';

export const AXIS_WIDTH = '3.5rem';
export const PX_PER_HOUR = 60;
export const PX_PER_HOUR_COMPACT = 40;

export interface SlotTarget {
  column: CalendarColumn;
  minute: number;
  providerId?: string;
}

export interface CalendarGridProps {
  snapshot: CalendarSnapshot;
  columns: CalendarColumn[];
  pxPerHour: number;
  now: { dayIndex: number; minute: number } | null;
  phone: boolean;
  activeId: string | null;
  pinned: boolean;
  settlingId: string | null;
  drag: DragState | null;
  canManage: boolean;
  onActivate: (id: string | null, pinned: boolean) => void;
  onSlotClick: (target: SlotTarget) => void;
  onChanged: () => void;
  registerColumn: (key: string, element: HTMLElement | null) => void;
  dragHandlers: {
    onPointerDown: (event: ReactPointerEvent<HTMLElement>, appointment: CalendarAppointment, columnKey: string, originMinute: number) => void;
    onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
    onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void;
    onPointerCancel: () => void;
  };
}

interface Placed {
  appointment: CalendarAppointment;
  provider: CalendarProvider;
  start: number;
  end: number;
}

interface ProviderStrip {
  provider: CalendarProvider;
  spans: SessionSpan[];
  index: number;
  strips: number;
}

interface ColumnCounts {
  booked: number;
  arrived: number;
  places: number;
}

function placedIn(snapshot: CalendarSnapshot, column: CalendarColumn, providersById: Map<string, CalendarProvider>): Placed[] {
  const day = snapshot.days[column.dayIndex];
  if (!day) return [];
  const wanted = new Set(column.providerIds);
  const out: Placed[] = [];
  for (const appointment of snapshot.appointments) {
    if (!wanted.has(appointment.providerId) || !isShownOnCalendar(appointment.status)) continue;
    if (!overlapsDay(day, appointment.startsAt, appointment.endsAt)) continue;
    const provider = providersById.get(appointment.providerId);
    if (!provider) continue;
    const start = Math.max(minuteOf(day, appointment.startsAt), snapshot.hours.fromMinute);
    const end = Math.min(Math.max(minuteOf(day, appointment.endsAt), start + 10), snapshot.hours.toMinute);
    if (end <= snapshot.hours.fromMinute || start >= snapshot.hours.toMinute) continue;
    out.push({ appointment, provider, start, end });
  }
  return out;
}

function stripsFor(day: CalendarDay, providers: CalendarProvider[]): ProviderStrip[] {
  const withSessions = providers
    .map((provider) => ({ provider, spans: providerSpans(day, provider.sessions) }))
    .filter((entry) => entry.spans.length > 0);
  return withSessions.map((entry, index) => ({ ...entry, index, strips: withSessions.length }));
}

function countsFor(placed: Placed[], strips: ProviderStrip[]): ColumnCounts {
  const places = strips.reduce((sum, strip) => sum + strip.spans.reduce((inner, span) => inner + slotRows(span).length * span.capacity, 0), 0);
  const arrived = placed.filter((item) => item.appointment.status === 'arrived' || item.appointment.status === 'in_consultation').length;
  return { booked: placed.length, arrived, places };
}

function ColumnHeader({ column, providers, counts }: { column: CalendarColumn; providers: CalendarProvider[]; counts: ColumnCounts }) {
  const single = providers.length === 1 ? providers[0] : undefined;
  const stacked = single ? [] : providers.slice(0, 3);
  return (
    <div className="flex min-w-0 items-center gap-2 border-b border-s bg-card/95 px-2 py-1.5 backdrop-blur">
      {single && (
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-xs font-semibold" style={avatarStyle(single.color)} aria-hidden>
          {initials(single.name)}
        </span>
      )}
      {stacked.length > 0 && (
        <span className="flex shrink-0 -space-x-2 rtl:space-x-reverse" aria-hidden>
          {stacked.map((provider) => (
            <span key={provider.id} className="grid h-6 w-6 place-items-center rounded-full text-[10px] font-semibold ring-2 ring-card" style={avatarStyle(provider.color)}>
              {initials(provider.name)}
            </span>
          ))}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{column.label}</p>
        {column.sublabel && <p className="truncate text-xs text-muted-foreground">{column.sublabel}</p>}
      </div>
      <div className="shrink-0 text-end text-[11px] leading-tight text-muted-foreground tabular" title={tr('opd', 'booked_x_of_y_places', { booked: counts.booked, places: counts.places })}>
        <p>
          <span className="font-medium text-foreground">{counts.booked}</span>
          {counts.places > 0 && <span> / {counts.places}</span>}
        </p>
        {counts.arrived > 0 && <p className="text-chart-2">{tr('opd', 'x_arrived', { count: counts.arrived })}</p>}
      </div>
    </div>
  );
}

interface SlotRowsProps {
  strip: ProviderStrip;
  span: SessionSpan;
  rows: SlotRow[];
  occupancy: number[];
  day: CalendarDay;
  nowMs: number;
  hours: CalendarSnapshot['hours'];
  pxPerHour: number;
  canManage: boolean;
  reducedMotion: boolean;
  onPick: (minute: number, providerId: string) => void;
}

function SlotRows({ strip, span, rows, occupancy, day, nowMs, hours, pxPerHour, canManage, reducedMotion, onPick }: SlotRowsProps) {
  const geometry = stripGeometry(strip.index, strip.strips);
  return (
    <>
      {rows.map((row, index) => {
        const top = minuteToPx(Math.max(row.start, hours.fromMinute), hours, pxPerHour);
        const bottom = minuteToPx(Math.min(row.end, hours.toMinute), hours, pxPerHour);
        if (bottom <= top) return null;
        const booked = occupancy[index] ?? 0;
        const past = Date.parse(isoAtMinute(day, row.end)) <= nowMs;
        const open = canManage && !past && booked < span.capacity;
        const label = tr('opd', 'booked_x_of_y_places', { booked, places: span.capacity });
        const pickable = canManage && !past;
        const time = minuteLabel(row.start);
        const doctor = strip.provider.name;
        const spoken = `${open ? tr('opd', 'book_at_x_with_y', { time, doctor }) : tr('opd', 'slot_at_x_with_y', { time, doctor })} · ${label}`;
        return (
          <div
            key={row.start}
            data-slot={row.start}
            data-provider={strip.provider.id}
            title={`${open ? tr('opd', 'book_at_x', { time }) : time} · ${label}`}
            role={pickable ? 'button' : undefined}
            tabIndex={pickable ? 0 : undefined}
            aria-label={pickable ? spoken : undefined}
            className={cn(
              'group absolute border-t border-border/50',
              pickable &&
                'cursor-pointer outline-none hover:bg-accent/60 focus-visible:z-10 focus-visible:bg-accent/60 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring',
              past && 'bg-muted/30',
              !reducedMotion && 'transition-colors duration-100',
            )}
            style={{ top, height: bottom - top, insetInlineStart: `${geometry.startPct}%`, width: `${geometry.widthPct}%` }}
            onClick={(event) => {
              if (!canManage || event.target !== event.currentTarget) return;
              event.stopPropagation();
              onPick(row.start, strip.provider.id);
            }}
            onKeyDown={(event) => {
              if (!pickable || event.target !== event.currentTarget) return;
              if (event.key !== 'Enter' && event.key !== ' ') return;
              event.preventDefault();
              event.stopPropagation();
              onPick(row.start, strip.provider.id);
            }}
          >
            <span className="pointer-events-none absolute inset-y-0 end-1 hidden items-center gap-1 text-[10px] text-muted-foreground group-hover:flex group-focus-visible:flex">
              {open && <Plus className="h-3 w-3" aria-hidden />}
              <span className="tabular">
                {booked} / {span.capacity}
              </span>
            </span>
          </div>
        );
      })}
    </>
  );
}

export function CalendarGrid({
  snapshot,
  columns,
  pxPerHour,
  now,
  phone,
  activeId,
  pinned,
  settlingId,
  drag,
  canManage,
  onActivate,
  onSlotClick,
  onChanged,
  registerColumn,
  dragHandlers,
}: CalendarGridProps) {
  const reducedMotion = useReducedMotion();
  const providersById = useMemo(() => new Map(snapshot.providers.map((provider) => [provider.id, provider])), [snapshot.providers]);
  const height = gridHeight(snapshot.hours, pxPerHour);
  const hourLines = hourMarks(snapshot.hours);
  const labels = axisMarks(snapshot.hours, pxPerHour);
  const nowMs = Date.now();
  const scrollRef = useRef<HTMLDivElement>(null);

  const template = `${AXIS_WIDTH} repeat(${columns.length}, minmax(${phone ? '0' : '150px'}, 1fr))`;

  const prepared = useMemo(
    () =>
      columns.map((column) => {
        const day = snapshot.days[column.dayIndex];
        if (!day) return null;
        const placed = placedIn(snapshot, column, providersById);
        const lanes = layoutLanes(placed.map((item) => ({ id: item.appointment.id, start: item.start, end: item.end })));
        const providers = column.providerIds.map((id) => providersById.get(id)).filter((p): p is CalendarProvider => Boolean(p));
        const strips = stripsFor(day, providers);
        const counts = countsFor(placed, strips);
        const blocks = providers.flatMap((provider) =>
          provider.blocks.filter((block) => overlapsDay(day, block.startsAt, block.endsAt)).map((block) => ({ ...block, provider })),
        );
        const sessions = strips.flatMap((strip) =>
          strip.spans.map((span) => {
            const rows = slotRows(span);
            const own = placed.filter((item) => item.provider.id === strip.provider.id);
            return { strip, span, rows, occupancy: rowOccupancy(rows, own) };
          }),
        );
        return { column, day, placed, lanes, strips, counts, blocks, sessions };
      }),
    [columns, snapshot, providersById],
  );

  return (
    <div ref={scrollRef} className="max-h-[calc(100vh-14rem)] overflow-auto rounded-lg border bg-card">
      <div className="grid" style={{ gridTemplateColumns: template }}>
        <div className="sticky top-0 z-30 border-b bg-card" style={{ insetInlineStart: 0 }} />
        {prepared.map((entry, index) => (
          <div key={columns[index].key} className="sticky top-0 z-20">
            {entry && (
              <ColumnHeader
                column={entry.column}
                providers={entry.strips.length > 0 ? entry.strips.map((strip) => strip.provider) : entry.column.providerIds.map((id) => providersById.get(id)).filter((p): p is CalendarProvider => Boolean(p))}
                counts={entry.counts}
              />
            )}
          </div>
        ))}

        <div className="sticky z-20 border-e bg-card" style={{ insetInlineStart: 0, height }}>
          <div className="relative" style={{ height }}>
            {labels.map((minute) => (
              <span
                key={minute}
                className={cn('absolute w-full pe-1.5 text-end text-[11px] tabular', minute % 60 === 0 ? 'text-muted-foreground' : 'text-muted-foreground/60')}
                style={{ top: minuteToPx(minute, snapshot.hours, pxPerHour) - 7 }}
              >
                {minuteLabel(minute)}
              </span>
            ))}
          </div>
        </div>

        {prepared.map((entry) => {
          if (!entry) return null;
          const { column, day, placed, lanes, strips, blocks, sessions } = entry;
          const showNow = now && now.dayIndex === column.dayIndex;
          const ghost = drag?.target?.columnKey === column.key ? drag : null;
          const allSpans = strips.flatMap((strip) => strip.spans);

          return (
            <div
              key={column.key}
              ref={(element) => registerColumn(column.key, element)}
              data-column={column.key}
              className={cn('relative border-s', ghost && 'bg-accent/40')}
              style={{ height, ...sessionBackground('outside') }}
              onClick={(event) => {
                if (!canManage || event.target !== event.currentTarget) return;
                const rect = event.currentTarget.getBoundingClientRect();
                const minute = snapToSessions(pxToMinute(event.clientY - rect.top, snapshot.hours, pxPerHour), allSpans);
                onSlotClick({ column, minute });
              }}
            >
              {hourLines.map((minute) => (
                <div
                  key={minute}
                  className="pointer-events-none absolute inset-x-0 border-t border-border/70"
                  style={{ top: minuteToPx(minute, snapshot.hours, pxPerHour) }}
                />
              ))}
              {sessions.map(({ strip, span }) => {
                const start = Math.max(span.start, snapshot.hours.fromMinute);
                const end = Math.min(span.end, snapshot.hours.toMinute);
                if (end <= start) return null;
                const geometry = stripGeometry(strip.index, strip.strips);
                const session = strip.provider.sessions.find((candidate) => candidate.date === day.date && minuteOf(day, candidate.startsAt) === span.start);
                return (
                  <div
                    key={`${strip.provider.id}-${span.start}`}
                    className={cn('pointer-events-none absolute', strip.strips > 1 && 'border-s border-[hsl(var(--provider)/0.35)]')}
                    style={{
                      ...providerStyle(strip.provider.color),
                      top: minuteToPx(start, snapshot.hours, pxPerHour),
                      height: minuteToPx(end, snapshot.hours, pxPerHour) - minuteToPx(start, snapshot.hours, pxPerHour),
                      insetInlineStart: `${geometry.startPct}%`,
                      width: `${geometry.widthPct}%`,
                      ...sessionBackground('session'),
                      ...(strip.strips > 1 ? { backgroundImage: 'linear-gradient(hsl(var(--provider) / 0.06), hsl(var(--provider) / 0.06))' } : {}),
                    }}
                  >
                    {session?.roomLabel && <span className="absolute end-1 top-0.5 text-[10px] text-muted-foreground">{session.roomLabel}</span>}
                  </div>
                );
              })}
              {sessions.map(({ strip, span, rows, occupancy }) => (
                <SlotRows
                  key={`rows-${strip.provider.id}-${span.start}`}
                  strip={strip}
                  span={span}
                  rows={rows}
                  occupancy={occupancy}
                  day={day}
                  nowMs={nowMs}
                  hours={snapshot.hours}
                  pxPerHour={pxPerHour}
                  canManage={canManage}
                  reducedMotion={reducedMotion}
                  onPick={(minute, providerId) => onSlotClick({ column, minute, providerId })}
                />
              ))}
              {blocks.map((block) => {
                const start = Math.max(minuteOf(day, block.startsAt), snapshot.hours.fromMinute);
                const end = Math.min(minuteOf(day, block.endsAt), snapshot.hours.toMinute);
                if (end <= start) return null;
                const strip = strips.find((candidate) => candidate.provider.id === block.provider.id);
                const geometry = strip ? stripGeometry(strip.index, strip.strips) : { startPct: 0, widthPct: 100 };
                return (
                  <div
                    key={block.id}
                    className="pointer-events-none absolute z-[1] px-1 text-[10px] text-destructive"
                    style={{
                      top: minuteToPx(start, snapshot.hours, pxPerHour),
                      height: minuteToPx(end, snapshot.hours, pxPerHour) - minuteToPx(start, snapshot.hours, pxPerHour),
                      insetInlineStart: `${geometry.startPct}%`,
                      width: `${geometry.widthPct}%`,
                      ...sessionBackground('leave'),
                    }}
                  >
                    {humanize(block.reason)}
                  </div>
                );
              })}
              {placed.map((item) => {
                const slot = lanes.get(item.appointment.id) ?? { lane: 0, lanes: 1 };
                return (
                  <CalendarEvent
                    key={item.appointment.id}
                    appointment={item.appointment}
                    provider={item.provider}
                    slot={slot}
                    top={minuteToPx(item.start, snapshot.hours, pxPerHour)}
                    height={minuteToPx(item.end, snapshot.hours, pxPerHour) - minuteToPx(item.start, snapshot.hours, pxPerHour)}
                    active={activeId === item.appointment.id}
                    pinned={pinned}
                    phone={phone}
                    dragging={drag?.appointment.id === item.appointment.id}
                    settling={settlingId === item.appointment.id}
                    reducedMotion={reducedMotion}
                    canDrag={canManage && ['booked', 'confirmed', 'no_show'].includes(item.appointment.status)}
                    onActivate={onActivate}
                    onChanged={onChanged}
                    onPointerDown={(event) => dragHandlers.onPointerDown(event, item.appointment, column.key, item.start)}
                    onPointerMove={dragHandlers.onPointerMove}
                    onPointerUp={dragHandlers.onPointerUp}
                    onPointerCancel={dragHandlers.onPointerCancel}
                  />
                );
              })}
              {ghost?.target && (
                <div
                  className="pointer-events-none absolute inset-x-1 z-20 rounded-md border-2 border-dashed border-primary bg-primary/10 px-1.5 py-0.5 text-xs"
                  style={{
                    top: minuteToPx(ghost.target.startMinute, snapshot.hours, pxPerHour),
                    height: Math.max((Math.max(ghost.appointment.durationMinutes, 10) / 60) * pxPerHour, 18),
                  }}
                >
                  {minuteLabel(ghost.target.startMinute)} · {ghost.appointment.patientName}
                </div>
              )}
              {showNow && now.minute >= snapshot.hours.fromMinute && now.minute <= snapshot.hours.toMinute && (
                <div className="pointer-events-none absolute inset-x-0 z-20 border-t-2 border-destructive" style={{ top: minuteToPx(now.minute, snapshot.hours, pxPerHour) }}>
                  <span className="absolute -top-1 h-2 w-2 rounded-full bg-destructive" style={{ insetInlineStart: -4 } as CSSProperties} />
                </div>
              )}
              {strips.length === 0 && (
                <p className="pointer-events-none absolute inset-x-0 top-1/2 px-2 text-center text-xs text-muted-foreground">{tr('opd', 'no_clinic_on_this_day')}</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
